import { Link } from "react-router-dom";
import { PageContainer } from "./PageContainer";
import { trpc } from "@/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Users,
  CalendarCheck,
  ArrowRight,
  TriangleAlert,
  Bike,
  Images,
  MessageSquare,
} from "lucide-react";
import { formatTimestamp } from "@/lib/date-utils";
import { useRecentEventFeedback } from "@/queries/event-feedback";
import { usePendingRegistrationCount } from "@/queries/users";
import { StarRow } from "@/components/events/StarRow";
import { BadgerAttendeesChartCard } from "./BadgerAttendeesChartCard";

function PendingAccountRegistrationsCard() {
  const { count, isLoading } = usePendingRegistrationCount();
  const hasPending = !isLoading && count > 0;

  return (
    <Card className={hasPending ? "border-yellow-400" : undefined}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Pending Account Registrations
        </CardTitle>
        <Users className={`size-4 ${hasPending ? "text-yellow-500" : "text-muted-foreground"}`} />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-8 w-12 animate-pulse rounded bg-muted" />
        ) : (
          <>
            <p className={`text-3xl font-bold ${hasPending ? "text-yellow-500" : ""}`}>{count}</p>
            {hasPending && (
              <div className="mt-2 flex items-center gap-1.5 rounded-md bg-yellow-50 px-2.5 py-1.5 text-xs font-medium text-yellow-800">
                <TriangleAlert className="size-3.5 shrink-0" />
                {count === 1
                  ? "1 registration ready for review"
                  : `${count} registrations ready for review`}
              </div>
            )}
          </>
        )}
        <Link
          to="/admin/contacts/users/pending"
          className="mt-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          View pending registrations
          <ArrowRight className="size-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

function PendingEventRegistrationsCard() {
  const { data, isLoading } = trpc.admin.rsvps.listPendingByAllEvents.useQuery();
  const events = data ?? [];
  const totalPending = events.reduce((sum, ev) => sum + ev.pendingCount, 0);
  const hasPending = !isLoading && totalPending > 0;

  return (
    <Card className={hasPending ? "border-yellow-400" : undefined}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Pending Event Registrations
        </CardTitle>
        <CalendarCheck
          className={`size-4 ${hasPending ? "text-yellow-500" : "text-muted-foreground"}`}
        />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {(["s0", "s1", "s2"] as const).map((k) => (
              <div key={k} className="h-5 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : (
          <>
            <p className={`text-3xl font-bold ${hasPending ? "text-yellow-500" : ""}`}>
              {totalPending}
            </p>
            {hasPending && (
              <div className="mt-2 flex items-center gap-1.5 rounded-md bg-yellow-50 px-2.5 py-1.5 text-xs font-medium text-yellow-800">
                <TriangleAlert className="size-3.5 shrink-0" />
                {events.length === 1
                  ? "1 event needs attention"
                  : `${events.length} events need attention`}
              </div>
            )}
            {events.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {events.map((ev) => (
                  <li key={ev.eventId} className="flex items-center justify-between text-sm">
                    <Link
                      to={`/admin/events/${ev.eventId}/attendees?payment=pending&status=yes`}
                      className="font-medium hover:underline truncate max-w-[200px]"
                      title={ev.eventName}
                    >
                      {ev.eventName}
                    </Link>
                    <span className="ml-2 shrink-0 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800">
                      {ev.pendingCount}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">No pending registrations.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PendingBikeSuggestionsCard() {
  const { data, isLoading } = trpc.admin.bikeSuggestions.pendingCount.useQuery();
  const count = data?.count ?? 0;
  const hasPending = !isLoading && count > 0;

  return (
    <Card className={hasPending ? "border-yellow-400" : undefined}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Pending Garage Suggestions
        </CardTitle>
        <Bike className={`size-4 ${hasPending ? "text-yellow-500" : "text-muted-foreground"}`} />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-8 w-12 animate-pulse rounded bg-muted" />
        ) : (
          <>
            <p className={`text-3xl font-bold ${hasPending ? "text-yellow-500" : ""}`}>{count}</p>
            {hasPending && (
              <div className="mt-2 flex items-center gap-1.5 rounded-md bg-yellow-50 px-2.5 py-1.5 text-xs font-medium text-yellow-800">
                <TriangleAlert className="size-3.5 shrink-0" />
                {count === 1 ? "1 suggestion needs review" : `${count} suggestions need review`}
              </div>
            )}
          </>
        )}
        <Link
          to="/admin/garage/suggestions"
          className="mt-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          View garage suggestions
          <ArrowRight className="size-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

function PendingMediaCard() {
  const { data, isLoading } = trpc.admin.media.pendingCount.useQuery();
  const count = data?.count ?? 0;
  const hasPending = !isLoading && count > 0;

  return (
    <Card className={hasPending ? "border-yellow-400" : undefined}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">Pending Media</CardTitle>
        <Images className={`size-4 ${hasPending ? "text-yellow-500" : "text-muted-foreground"}`} />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-8 w-12 animate-pulse rounded bg-muted" />
        ) : (
          <>
            <p className={`text-3xl font-bold ${hasPending ? "text-yellow-500" : ""}`}>{count}</p>
            {hasPending && (
              <div className="mt-2 flex items-center gap-1.5 rounded-md bg-yellow-50 px-2.5 py-1.5 text-xs font-medium text-yellow-800">
                <TriangleAlert className="size-3.5 shrink-0" />
                {count === 1 ? "1 photo needs review" : `${count} photos need review`}
              </div>
            )}
          </>
        )}
        <Link
          to="/admin/media"
          className="mt-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          View pending media
          <ArrowRight className="size-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

/**
 * The latest event feedback across all events. Feedback sends no admin
 * notification, so this is where new ratings surface; each one links to its
 * event's Feedback section. Not a review queue, so no attention styling.
 */
function RecentFeedbackCard() {
  const { data, isLoading } = useRecentEventFeedback();
  const items = data ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">Recent Feedback</CardTitle>
        <MessageSquare className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {(["s0", "s1", "s2"] as const).map((k) => (
              <div key={k} className="h-5 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No feedback yet.</p>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={`${item.event_id}:${item.contact_id}`} className="space-y-0.5 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{item.display_name}</span>
                  <StarRow
                    value={item.rating}
                    label={`${item.display_name} rated ${item.event_name} ${item.rating} out of 5`}
                    className="size-3.5"
                  />
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <Link
                    to={`/admin/events/${item.event_id}#feedback`}
                    className="truncate hover:text-foreground hover:underline"
                    title={item.event_name}
                  >
                    {item.event_name}
                  </Link>
                  <span className="shrink-0">{formatTimestamp(item.updated_at)}</span>
                </div>
                {item.comment && (
                  <p className="line-clamp-2 text-muted-foreground">{item.comment}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function HomePage() {
  return (
    <PageContainer>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <PendingAccountRegistrationsCard />
        <PendingEventRegistrationsCard />
        <PendingBikeSuggestionsCard />
        <PendingMediaCard />
        <RecentFeedbackCard />
      </div>
      <BadgerAttendeesChartCard />
    </PageContainer>
  );
}
